<template>
  <div class="accounting-entry-source" :class="{ 'accounting-entry-source--deleted': source && source.deleted }" v-if="loading || source">
    <div class="accounting-entry-source__loading" v-if="loading"><b-spinner small class="mr-2" /> Cargando operación de origen...</div>
    <template v-else>
      <div class="accounting-entry-source__header">
        <span class="accounting-entry-source__eyebrow">Operación de origen<template v-if="titleRow"> · {{ titleRow.label }}</template></span>
        <div class="accounting-entry-source__actions">
          <b-badge variant="danger" v-if="source.deleted">Dada de baja</b-badge>
          <b-button size="sm" variant="outline-dark" v-if="source.report" @click="print()"><AppIcon name="print" class="mr-1" />Ver comprobante</b-button>
          <b-button size="sm" variant="link" class="accounting-entry-source__close" v-if="$listeners.close" v-b-tooltip.hover title="Cerrar" aria-label="Cerrar" @click="$emit('close')"><AppIcon name="close" /></b-button>
        </div>
      </div>

      <div class="accounting-entry-source__body">
        <div class="accounting-entry-source__identity">
          <strong class="accounting-entry-source__title" v-if="titleRow">{{ titleRow.value }}</strong>
          <div class="accounting-entry-source__details" v-if="detailRows.length">
            <span v-for="row in detailRows" :key="row.label"><small>{{ row.label }}</small> {{ row.value }}</span>
          </div>
        </div>
        <div class="accounting-entry-source__amounts" v-if="amountRows.length">
          <div v-for="(row, index) in amountRows" :key="row.label" class="accounting-entry-source__amount" :class="{ 'accounting-entry-source__amount--main': index == amountRows.length - 1 }">
            <small>{{ row.label }}</small>
            <span>{{ row.value }}</span>
          </div>
        </div>
      </div>

      <div class="accounting-entry-source__note" v-for="row in noteRows" :key="row.label" :title="row.value"><small>{{ row.label }}</small> {{ row.value }}</div>
    </template>
    <Printer v-if="printer.id" :reference="printer.reference" :registerID="printer.id" :key="printer.key" />
  </div>
</template>
<script>
  import Printer from '@/components/inc/printer/printer'
  import serviceAPI from '../services'
  import Error from '@/handler/error'

  // Etiquetas de las filas que arma AccountingEntrySource.php en el backend
  const AMOUNT_LABELS = ['Neto', 'IVA', 'Total', 'Importe']
  const NOTE_LABELS = ['Observaciones', 'Detalle']

  export default {
    components: {
      Printer,
    },
    props: {
      accountingId: {
        type: Number,
        required: true,
      },
    },
    data: () => {
      return {
        source: null,
        loading: false,
        printer: { key: 0, id: 0, reference: '' },
      }
    },
    computed: {
      rows() {
        return this.source ? this.source.rows : []
      },
      titleRow() {
        return this.rows[0] || null
      },
      amountRows() {
        return this.rows.filter(row => AMOUNT_LABELS.indexOf(row.label) >= 0)
      },
      noteRows() {
        return this.rows.filter(row => NOTE_LABELS.indexOf(row.label) >= 0)
      },
      detailRows() {
        return this.rows.slice(1).filter(row => AMOUNT_LABELS.indexOf(row.label) < 0 && NOTE_LABELS.indexOf(row.label) < 0)
      },
    },
    watch: {
      accountingId() {
        this.load()
      },
    },
    mounted() {
      this.load()
    },
    methods: {
      load() {
        const accountingId = this.accountingId
        this.source = null
        this.loading = true
        serviceAPI.origenAsiento(accountingId)
        .then(response => {
          // si mientras tanto se eligió otra operación, esta respuesta ya no vale
          if(accountingId != this.accountingId) return
          this.loading = false
          this.source = response.data
          this.$nextTick(() => this.$emit('loaded'))
        })
        .catch(error => {
          if(accountingId != this.accountingId) return
          this.loading = false
          this.$awn.alert(Error.showError(error))
        })
      },
      print() {
        this.printer.key = Math.random()
        this.printer.reference = this.source.report
        this.printer.id = this.source.id
      },
    }
  }
</script>
<style scoped>
  .accounting-entry-source {
    background: var(--app-color-surface);
    border: 1px solid var(--app-color-info-border);
    border-left: 3px solid var(--app-color-primary);
    border-radius: 8px;
    box-shadow: 0 3px 10px rgba(7, 58, 78, 0.08);
    margin-bottom: 12px;
    padding: 10px 14px;
  }
  .accounting-entry-source--deleted { border-left-color: var(--app-color-danger); }
  .accounting-entry-source__loading { align-items: center; color: var(--app-color-text-secondary); display: flex; font-size: var(--app-text-sm); }
  .accounting-entry-source__header { align-items: center; display: flex; gap: 12px; justify-content: space-between; min-height: 24px; }
  .accounting-entry-source__eyebrow { color: var(--app-color-text-muted); font-size: 11px; font-weight: var(--app-font-semibold); letter-spacing: 0.04em; text-transform: uppercase; }
  .accounting-entry-source__actions { align-items: center; display: flex; flex-shrink: 0; gap: 8px; }
  .accounting-entry-source__close { color: var(--app-color-text-secondary); line-height: 1; padding: 0; }
  .accounting-entry-source__body { align-items: flex-end; display: flex; flex-wrap: wrap; gap: 8px 32px; justify-content: space-between; }
  .accounting-entry-source__identity { flex: 1 1 320px; min-width: 0; }
  .accounting-entry-source__title { color: var(--app-color-text); display: block; font-size: 16px; font-weight: var(--app-font-semibold); line-height: 1.3; }
  .accounting-entry-source__details { color: var(--app-color-text); display: flex; flex-wrap: wrap; font-size: var(--app-text-sm); gap: 2px 18px; margin-top: 3px; }
  .accounting-entry-source small { color: var(--app-color-text-secondary); font-size: var(--app-text-xs); }
  .accounting-entry-source__amounts { display: flex; flex-wrap: wrap; gap: 4px 24px; }
  .accounting-entry-source__amount { text-align: right; white-space: nowrap; }
  .accounting-entry-source__amount small,
  .accounting-entry-source__amount span { display: block; }
  .accounting-entry-source__amount span { color: var(--app-color-text); font-size: var(--app-text-sm); font-weight: var(--app-font-semibold); }
  .accounting-entry-source__amount--main span { color: var(--app-color-primary-active); font-size: 16px; }
  .accounting-entry-source__note { border-top: 1px solid var(--app-color-border); color: var(--app-color-text); display: -webkit-box; font-size: var(--app-text-sm); margin-top: 8px; overflow: hidden; padding-top: 6px; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
  @media (max-width: 575.98px) {
    .accounting-entry-source__amounts { justify-content: space-between; width: 100%; }
    .accounting-entry-source__amount { text-align: left; }
  }
</style>
